export type LapEvent = {
    event_id: number;
    event_time: string;
    duration: number;
    kind: 'work' | 'rest';
};

export type ShovelfulRecord = {
    value: number;
    timestamp: string | null;
};

export type SeriesBlock =
    | {
          type: 'series';
          index: number; // 1-based among work series
          laps: LapEvent[];
          lapDurations: number[];
          shovelfuls: number[];
          shovelfulsTimestamps: string[];
          totalDurationSeconds: number;
          avgShovelfuls: number | null;
      }
    | {
          type: 'rest';
          duration: number;
      };

const parseRelativeTimestampToSeconds = (ts: string | null): number | null => {
    if (!ts) return null;
    const parts = ts.split(':').map(Number);
    if (parts.length === 3) {
        return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }
    if (parts.length === 2) {
        return parts[0] * 60 + parts[1];
    }
    return null;
};

export function buildSeriesBlocks(
    lapEvents: LapEvent[] = [],
    shovelfulsData: ShovelfulRecord[] = []
): SeriesBlock[] {
    if (!lapEvents || lapEvents.length === 0) {
        return [];
    }

    // Compute each lap's offset from the start (in seconds).
    // Lap N's window is [offsetBefore, offsetBefore + duration).
    let runningOffset = 0;
    const lapWindows: { lap: LapEvent; startOffset: number; endOffset: number }[] = lapEvents.map(lap => {
        const startOffset = runningOffset;
        runningOffset += lap.duration;
        return { lap, startOffset, endOffset: runningOffset };
    });

    const blocks: SeriesBlock[] = [];
    let currentLaps: { lap: LapEvent; startOffset: number; endOffset: number }[] = [];
    let workIndex = 0;

    const flushSeries = () => {
        if (currentLaps.length === 0) return;
        workIndex++;
        const seriesStart = currentLaps[0].startOffset;
        const seriesEnd = currentLaps[currentLaps.length - 1].endOffset;
        const lapDurations = currentLaps.map(l => l.lap.duration);
        const totalDuration = lapDurations.reduce((a, b) => a + b, 0);

        const shovelfuls: number[] = [];
        const shovelfulsTimestamps: string[] = [];
        shovelfulsData.forEach(s => {
            const offset = parseRelativeTimestampToSeconds(s.timestamp);
            if (offset !== null && offset >= seriesStart && offset < seriesEnd && s.value > 0) {
                shovelfuls.push(s.value);
                shovelfulsTimestamps.push(s.timestamp || '');
            }
        });

        const avgShovelfuls = shovelfuls.length > 0
            ? shovelfuls.reduce((a, b) => a + b, 0) / shovelfuls.length
            : null;

        blocks.push({
            type: 'series',
            index: workIndex,
            laps: currentLaps.map(l => l.lap),
            lapDurations,
            shovelfuls,
            shovelfulsTimestamps,
            totalDurationSeconds: totalDuration,
            avgShovelfuls
        });
        currentLaps = [];
    };

    lapWindows.forEach(item => {
        if (item.lap.kind === 'rest') {
            flushSeries();
            blocks.push({ type: 'rest', duration: item.lap.duration });
        } else {
            currentLaps.push(item);
        }
    });
    flushSeries();

    return blocks;
}

export function formatDuration(seconds: number, includeMs: boolean = false): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (includeMs) {
        const millis = Math.round((seconds - Math.floor(seconds)) * 1000);
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${millis.toString().padStart(3, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}
